/**
 * 録音送信が失敗したときの原因特定を助ける情報を作る。
 *
 * 2026-09-09〜09-12 の障害では、CSP の connect-src が署名付き URL への PUT を
 * ブロックしていた。画面には `[put(upload)] Failed to fetch` としか出ないため、
 * 3 日間 原因が分からないまま利用者がリトライを続けた（API #63）。
 *
 * `Failed to fetch` は「HTTP のステータスが 1 つも返ってこなかった」ことしか
 * 示さない。CSP でブロックされたのか、CORS で落ちたのか、単に届かなかったのかは
 * 区別が付かない。そこで、
 *
 *   - 送信先のホスト名（環境依存で、設定ミスがここに出る）
 *   - 直前に CSP 違反が起きたかどうか（起きていれば原因はほぼ確定する）
 *
 * を添えて、遭遇した人がその場で原因を掴めるようにする。
 * BE 側の異常検知は「誰も報告しなくても分かる」ための仕組みで、役割が違う。
 */

/**
 * CSP 違反イベントの発火を待つ時間(ms)。
 * fetch の reject が先、違反イベントが後という順序のため、ごく短く待つ。
 */
const VIOLATION_SETTLE_MS = 50;

/** 直近に観測した CSP 違反（connect-src のみ） */
export interface CspViolation {
  blockedUri: string;
  directive: string;
  at: number;
}

let lastViolation: CspViolation | null = null;
let listening = false;

/**
 * CSP 違反の監視を始める。録音画面の表示中だけ動かす。
 * connect-src 以外（画像やスタイル）は送信失敗と無関係なので拾わない。
 */
export function startCspViolationWatch(): () => void {
  if (typeof document === 'undefined') return () => {};
  if (listening) return () => {};
  const handler = (e: SecurityPolicyViolationEvent) => {
    if (!e.violatedDirective?.startsWith('connect-src')) return;
    lastViolation = {
      blockedUri: e.blockedURI,
      directive: e.violatedDirective,
      at: Date.now(),
    };
  };
  document.addEventListener('securitypolicyviolation', handler);
  listening = true;
  return () => {
    document.removeEventListener('securitypolicyviolation', handler);
    listening = false;
    lastViolation = null;
  };
}

/** テスト用。監視状態を初期化する */
export function resetCspViolationWatch(): void {
  lastViolation = null;
}

/** URL からホスト名だけを取り出す（相対 URL やパース不能な値も落とさない） */
export function hostOf(url: string): string {
  try {
    return new URL(url, window.location.origin).host;
  } catch {
    return '(不明)';
  }
}

/**
 * アップロード失敗時の追加情報を組み立てる。
 * 表示できる材料が無ければ null を返し、呼び出し側は従来どおりのメッセージを出す。
 *
 * @param uploadUrl 署名付き URL（BE が返した値）
 * @param withinMs  直前の CSP 違反とみなす時間幅
 */
export async function buildUploadFailureDetail(
  uploadUrl: string | null | undefined,
  withinMs = 10_000
): Promise<{ host: string; cspBlocked: boolean } | null> {
  if (!uploadUrl) return null;
  const host = hostOf(uploadUrl);
  /*
   * securitypolicyviolation は fetch の reject より後に発火する。
   * reject 直後に見に行くと、CSP でブロックされていても記録がまだ無い。
   * 失敗経路なので、ここで少し待っても利用者の体感には影響しない。
   */
  await new Promise((resolve) => setTimeout(resolve, VIOLATION_SETTLE_MS));
  const v = lastViolation;
  const cspBlocked =
    v !== null && Date.now() - v.at <= withinMs && hostOf(v.blockedUri) === host;
  return { host, cspBlocked };
}
